import { useEffect, useState } from "react";
import { api, errText, photoUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, Plus, Check, AlertTriangle } from "lucide-react";
import { PLACEMENTS, PLACEMENT_LABEL, IDEAL_SIZE, emptyForm, PLACEMENT_RATIO } from "./adShared";

export function AdCreateForm({ weights, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adImgDims, setAdImgDims] = useState(null);
  useEffect(() => { setAdImgDims(null); }, [form.image]);
  const onImage = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.image) return toast.error("Upload an ad image first");
    if (!form.link_url.trim()) return toast.error("Add a destination link");
    setSaving(true);
    try {
      await api.post("/admin/ads", {
        title: form.title.trim(), image: form.image, link_url: form.link_url.trim(),
        placement: form.placement, active: form.active, weight: Number(form.weight) || 1,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : "",
        end_at: form.end_at ? new Date(form.end_at).toISOString() : "",
      });
      toast.success("Ad created");
      setForm(emptyForm);
      onCreated();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 self-start" data-testid="ad-create-form">
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-4"><Plus size={18} className="text-[#F5551E]" /> New ad</h3>

          <Label className="text-xs font-semibold text-slate-500">Ad image</Label>
          <p className="text-[11px] text-slate-400 mb-1" data-testid="ad-size-hint">Recommended for {PLACEMENT_LABEL[form.placement]}: <span className="font-semibold text-[#F5551E]">{IDEAL_SIZE[form.placement]}</span></p>
          {form.image ? (
            (() => {
              const target = PLACEMENT_RATIO[form.placement] || 1;
              const isHomePrev = form.placement === "home" || form.placement === "home_top";
              const willCrop = !isHomePrev && adImgDims ? Math.abs(adImgDims.w / adImgDims.h - target) / target > 0.08 : false;
              return (
                <div className="mt-1.5">
                  <div
                    className={`relative rounded-xl overflow-hidden border-2 border-dashed border-[#F5551E] ${isHomePrev ? "bg-slate-100 flex items-center justify-center" : "bg-[#FFF4F0]"}`}
                    style={{ aspectRatio: `${target}` }}
                    data-testid="ad-image-preview"
                  >
                    <img
                      src={photoUrl(form.image)}
                      onLoad={(e) => setAdImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                      alt=""
                      className={`w-full h-full ${isHomePrev ? "object-contain" : "object-cover"}`}
                    />
                    {!isHomePrev && <span className="pointer-events-none absolute inset-[7%] border border-dashed border-white/85 rounded-sm" data-testid="ad-safe-area" />}
                    {!isHomePrev && <span className="pointer-events-none absolute bottom-1 left-1 text-[8px] font-bold text-white/90 bg-black/45 rounded px-1">Safe area</span>}
                    <button onClick={() => setForm((f) => ({ ...f, image: "" }))} data-testid="ad-remove-image" className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"><Trash2 size={14} /></button>
                  </div>
                  {isHomePrev ? (
                    <p className="mt-1.5 text-[10px] text-emerald-700 flex items-center gap-1" data-testid="ad-fit-ok"><Check size={12} /> Shown in full on Home — fixed {IDEAL_SIZE.home} banner, never cropped.</p>
                  ) : willCrop ? (
                    <div className="mt-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1 flex items-start gap-1.5" data-testid="ad-crop-warning">
                      <AlertTriangle size={12} className="text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-[10px] text-amber-800">{adImgDims?.w}×{adImgDims?.h} will be centre-cropped for {PLACEMENT_LABEL[form.placement]}. Keep key content in the safe area or upload <strong>{IDEAL_SIZE[form.placement]}</strong>.</p>
                    </div>
                  ) : adImgDims ? (
                    <p className="mt-1.5 text-[10px] text-emerald-700 flex items-center gap-1" data-testid="ad-fit-ok"><Check size={12} /> Good fit for {PLACEMENT_LABEL[form.placement]} ({IDEAL_SIZE[form.placement]}).</p>
                  ) : null}
                </div>
              );
            })()
          ) : (
            <label className="mt-1.5 flex flex-col items-center justify-center gap-1 h-32 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#F5551E] hover:bg-[#FFF9F6] transition-colors" data-testid="ad-image-upload">
              {uploading ? <Loader2 size={20} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={20} className="text-slate-400" />}
              <span className="text-xs text-slate-400">{uploading ? "Uploading…" : "Click to upload ad creative"}</span>
              <input type="file" accept="image/*" onChange={onImage} className="hidden" />
            </label>
          )}

          <div className="mt-4">
            <Label className="text-xs font-semibold text-slate-500">Title (optional overlay)</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. 20% off premium dog food" data-testid="ad-title-input" className="mt-1.5 rounded-xl" maxLength={80} />
          </div>

          <div className="mt-4">
            <Label className="text-xs font-semibold text-slate-500">Destination link</Label>
            <Input value={form.link_url} onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))} placeholder="https://… or /browse" data-testid="ad-link-input" className="mt-1.5 rounded-xl" />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500">Placement</Label>
              <Select value={form.placement} onValueChange={(v) => setForm((f) => ({ ...f, placement: v, weight: weights?.weights?.[v] ?? f.weight }))}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="ad-placement-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLACEMENTS.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`ad-placement-${p.value}`}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Rotation weight (1–10)</Label>
              <Input type="number" min={1} max={10} value={form.weight} onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))} data-testid="ad-weight-input" className="mt-1.5 rounded-xl" />
              {weights?.weights && <p className="text-[10px] text-slate-400 mt-1">Default for {PLACEMENT_LABEL[form.placement]}: {weights.weights[form.placement]}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500">Start (optional)</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} data-testid="ad-start-input" className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">End (optional)</Label>
              <Input type="datetime-local" value={form.end_at} onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))} data-testid="ad-end-input" className="mt-1.5 rounded-xl" />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <Label className="text-sm text-slate-600">Active</Label>
            <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="ad-active-switch" />
          </div>

          <Button onClick={create} disabled={saving || !form.image || !form.link_url.trim()} data-testid="ad-create-btn" className="mt-5 w-full rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2 disabled:opacity-40">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create ad
          </Button>
        </div>
  );
}
